import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import {
    Component,
    OnInit,
    ViewEncapsulation,
    OnDestroy,
    ChangeDetectorRef,
    ViewChild,
    ElementRef,
    Inject,
    Optional,
    inject,
} from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { delay, takeUntil } from "rxjs/operators";
import { Actions, ofType } from "@ngrx/effects";
import { NotifyService } from "@ui/notify";
import { ProtocolFacade } from "../store/facade";
import { FormBuilder, FormGroup } from "@angular/forms";
import { createProtocolAction } from "../store/actions";
import { CreateProtocolComponentStore } from "./create-template-component-store";
import { assign } from "lodash";
import { RecurrencePickerDialogComponent, RecurrenceHelper, VuiValidators } from "@vui";
import { FuseConfirmationService } from "@nx-workspace/fuse";
import { TranslocoService } from "@jsverse/transloco";
import { FeedStatus } from "../store";
import { ActivityItemDefinition, FormItemDefinition, ProtocolDefinition } from "../../model";
import { SmartFormPickerDialogComponent } from "../../form-pool/form-definition-picker/smart-form-picker-dialog.component";
import { ReminderPickerDialogComponent } from "../../reminder-pool/reminder-picker/reminder-picker-dialog.component";
import { DocumentStatus } from "../../api";

@Component({
    selector: "create-template",
    templateUrl: "./create-template.component.html",
    encapsulation: ViewEncapsulation.None,
    providers: [CreateProtocolComponentStore],
})
export class CreateProtocolComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject();

    private _store = inject(CreateProtocolComponentStore);

    isCreating$: Observable<boolean>;

    form: FormGroup;

    formItems$ = this._store.formItems$;

    activityItems$ = this._store.activityItems$;

    readonly validProtocols$ = this._store.isValid$;

    readonly editing$ = this._store.editing$;

    readonly editingItem$ = this._store.editingItem$;

    readonly editingForm$ = this._store.editingForm$;

    currentActivityForm: FormGroup;

    @ViewChild("activityTitle", { read: ElementRef }) activityTitleInput: ElementRef;

    feedStatuses = [FeedStatus.Visible, FeedStatus.PatientHidden];

    constructor(
        private _matDialog: MatDialog,
        private _changeDetector: ChangeDetectorRef,
        private _dialogRef: MatDialogRef<CreateProtocolComponent>,
        private _formBuilder: FormBuilder,
        private _facade: ProtocolFacade,
        private actions$: Actions,
        private _notifyService: NotifyService,
        private _translateService: TranslocoService,
        private _confirmationService: FuseConfirmationService,
        @Inject(MAT_DIALOG_DATA) @Optional() private _data?: ProtocolDefinition
    ) {
        this.isCreating$ = this._facade.creating$;
        this.form = this._formBuilder.group({
            name: [null, VuiValidators.required],
            isPublic: [true],
        });

        this.currentActivityForm = this._formBuilder.group({
            id: [null, VuiValidators.required],
            title: [null, VuiValidators.required],
            activityId: [null],
            formId: [null],
            formName: [null],
            comments: [null],
            recurrence: [null],
            feedStatus: [FeedStatus.Visible, VuiValidators.required],
            documentStatus: [DocumentStatus.Visible, VuiValidators.required],
        });

        if (this._data.id) {
            this.form.patchValue({
                name: this._data.name,
                isPublic: this._data.isPublic,
            });

            for (const activity of this._data?.items) {
                switch (activity.discriminator) {
                    case "FormItemDefinitionDto":
                        let formItem = activity as FormItemDefinition;
                        this._store.addForm();
                        this.currentActivityForm.patchValue({
                            id: this._store.currentId,
                            title: formItem.title,
                            comments: formItem.comments,
                            formId: formItem.form.id,
                            formName: formItem.form.name,
                            recurrence: formItem.recurrence,
                            feedStatus: formItem.feedStatus,
                            documentStatus: formItem.documentStatus,
                        });
                        this.acceptFormChanges(this.currentActivityForm.getRawValue());
                        break;

                    case "ActivityItemDefinitionDto":
                        let activityItem = activity as ActivityItemDefinition;
                        this._store.addActivity();
                        this.currentActivityForm.patchValue({
                            id: this._store.currentId,
                            title: activityItem.title,
                            comments: activityItem.comments,
                            recurrence: activityItem.recurrence,
                            feedStatus: activityItem.feedStatus,
                            documentStatus: activityItem.documentStatus,
                        });
                        this.acceptActivityChanges(this.currentActivityForm.getRawValue());
                        break;
                    default:
                        break;
                }
            }
        }
    }

    ngOnInit() {
        this.actions$.pipe(ofType(createProtocolAction.complete), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._notifyService.success(
                this._translateService.translate("health.engage.protocol-pool.create-protocol.successfully-message")
            );
            this._dialogRef.close();
        });

        this._store.editingItem$.pipe(takeUntil(this._unsubscribeAll)).subscribe((activity: ActivityItemDefinition) => {
            this.currentActivityForm.get("formId").clearValidators();
            this.currentActivityForm.get("formId").updateValueAndValidity();

            if (activity) {
                this.currentActivityForm.patchValue({
                    ...activity,
                    id: this._store.currentId,
                });

                //Focus on the title element
                of(true)
                    .pipe(delay(150))
                    .subscribe(() => {
                        this.activityTitleInput.nativeElement.focus();
                    });
            } else {
                this.currentActivityForm.reset();
                this.currentActivityForm.patchValue({ delivered: true });
            }
            this._changeDetector.detectChanges();
        });

        this._store.editingForm$.pipe(takeUntil(this._unsubscribeAll)).subscribe((activity: FormItemDefinition) => {
            this.currentActivityForm.get("formId").setValidators(VuiValidators.required);
            this.currentActivityForm.get("formId").updateValueAndValidity();
            if (activity) {
                this.currentActivityForm.patchValue({
                    ...activity,
                    formId: activity.form?.id,
                    formName: activity.form?.name,
                    id: this._store.currentId,
                });

                //Focus on the title element
                of(true)
                    .pipe(delay(150))
                    .subscribe(() => {
                        this.activityTitleInput.nativeElement.focus();
                    });
            } else {
                this.currentActivityForm.reset();
                this.currentActivityForm.patchValue({ delivered: true });
            }
            this._changeDetector.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    addForm() {
        this._store.addForm();
        this._matDialog
            .open(SmartFormPickerDialogComponent)
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.currentActivityForm.patchValue({
                        title: result.name || result.title,
                        comments: result.comments,
                        formId: result.id,
                        formName: result.name,
                        feedStatus: result.feedStatus,
                    });
                }
            });
    }

    startWithActivityTemplate(event) {
        if (event instanceof MouseEvent) {
            event.stopPropagation();
        }
        this._matDialog
            .open(ReminderPickerDialogComponent)
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this._store.addActivity();
                    this.currentActivityForm.patchValue({
                        title: result.title,
                        comments: result.comments,
                        activityId: result.id,
                        feedStatus: result.feedStatus,
                    });
                }
            });
    }

    addActivity() {
        this._store.addActivity();
    }

    editForm() {
        this._matDialog
            .open(SmartFormPickerDialogComponent)
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.currentActivityForm.patchValue({
                        title: result.title,
                        comments: result.comments,
                        formId: result.id,
                        formName: result.name,
                        feedStatus: result.feedStatus,
                    });
                }
            });
    }

    updateRecurrence(activity: FormItemDefinition | ActivityItemDefinition) {
        let ref = this._matDialog.open(RecurrencePickerDialogComponent, {
            data: {
                recurrence: activity.recurrence,
                stampStartDate: false,
            },
        });

        ref.afterClosed().subscribe((recurrence: string | undefined) => {
            if (recurrence) {
                this.currentActivityForm.patchValue({ recurrence });
                activity.recurrence = recurrence;
                this._changeDetector.markForCheck();
            }
        });
    }

    parseRecurrenceText(activity: FormItemDefinition | ActivityItemDefinition) {
        return RecurrenceHelper.parseRecurrenceMessage(activity.recurrence, false);
    }

    acceptFormChanges(activity: FormItemDefinition) {
        let newActivity = assign(activity, {
            ...this.currentActivityForm.getRawValue(),
            form: {
                id: this.currentActivityForm.get("formId").value,
                name: this.currentActivityForm.get("formName").value,
            },
        });
        this._store.acceptFormChanges(newActivity);
    }

    acceptActivityChanges(activity: ActivityItemDefinition) {
        let newActivity = assign(activity, {
            ...activity,
            activityId: this.currentActivityForm.get("activityId").value,
            title: this.currentActivityForm.get("title").value,
            comments: this.currentActivityForm.get("comments").value,
            recurrence: this.currentActivityForm.get("recurrence").value,
            feedStatus: this.currentActivityForm.get("feedStatus").value,
            documentStatus: this.currentActivityForm.get("documentStatus").value,
            id: this.currentActivityForm.get("id").value,
        });
        this._store.acceptActivityChanges(newActivity);
    }

    cancelFormEdition(activity: FormItemDefinition) {
        this._store.cancelFormEdition(activity);
    }

    cancelItemEdition(activity: ActivityItemDefinition) {
        this._store.cancelItemEdition(activity);
    }

    startFormEdition(activity: FormItemDefinition) {
        assign(new FormItemDefinition(), {
            ...activity,
            form: {
                id: this.currentActivityForm.get("formId").value,
                name: this.currentActivityForm.get("formName").value,
            },
        });

        this._store.startFormEdition(activity);
    }

    startActivityEdition(activity: ActivityItemDefinition) {
        this._store.startActivityEdition(activity);
    }

    deleteForm(event: any, activity: FormItemDefinition) {
        event.stopPropagation();
        this._confirmationService
            .open({
                title: this._translateService.translate(
                    "health.engage.protocol-pool.create-protocol.form-deletion-alert-title"
                ),
                message: this._translateService.translate(
                    "health.engage.protocol-pool.create-protocol.form-deletion-alert-message",
                    { name: activity.title }
                ),
                actions: {
                    confirm: {
                        label: this._translateService.translate("actions.yes"),
                    },
                    cancel: {
                        label: this._translateService.translate("actions.no"),
                    },
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result === "confirmed") {
                    this._store.deleteForm(activity);
                }
            });
    }

    deleteActivity(event: any, activity: ActivityItemDefinition) {
        event.stopPropagation();
        this._confirmationService
            .open({
                title: this._translateService.translate(
                    "health.engage.protocol-pool.create-protocol.reminder-deletion-alert-title"
                ),
                message: this._translateService.translate(
                    "health.engage.protocol-pool.create-protocol.reminder-deletion-alert-message",
                    { name: activity.title }
                ),
                actions: {
                    confirm: {
                        label: this._translateService.translate("actions.yes"),
                    },
                    cancel: {
                        label: this._translateService.translate("actions.no"),
                    },
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result === "confirmed") {
                    this._store.deleteActivity(activity);
                }
            });
    }

    save() {
        if (this.form.valid) {
            let data = this.form.getRawValue();
            this._store.save(data);
        }
    }

    closeDialog() {
        this._dialogRef.close();
    }
}
